<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type CustomerUser,
    type AdvowareSettingsRequest,
    RoleEnum,
    SettingsService,
    type AdvowareSettings
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { toast } from '@/components/ui/toast'
  import { HttpStatus } from '@/constants/settings/HttpStatus'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Advoware integration fields
  const isAdvowareEnabled = ref(false)
  const advowareLawfirmId = ref('')
  const advowareDatabase = ref('')
  const advowareUser = ref('')
  const advowarePassword = ref('')
  const originalAdvowarePassword = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description: 'Configure Advoware integration settings for JUPUS.',
      enableAdvoware: 'Enable Advoware Integration',
      advowareLawfirmId: 'Lawfirm Id',
      advowareLawfirmIdPlaceholder: 'Enter Lawfirm Id',
      advowareDatabase: 'Database',
      advowareDatabasePlaceholder: 'Enter Database',
      advowareUser: 'User (short form)',
      advowareUserPlaceholder: 'Enter the user in short form',
      advowarePassword: 'Password',
      advowarePasswordPlaceholder: 'Enter Password',
      saving: 'Saving...',
      syncAdvoware: 'Sync Advoware',
      success: 'Success',
      syncSuccessMsg: 'Advoware was successfully synced.',
      syncFailedMsg: 'Advoware sync failed',
      advowareAuthError:
        'Advoware authentication failed. This may be due to incorrect credentials, the Advoware relay not being connected, or network connectivity issues. Please verify your settings under Settings → Law Office Software → Advoware and ensure your Advoware system is accessible.',
      advowareApiError:
        'Advoware API error: There was a problem communicating with the Advoware interface. Please check your connection and settings.'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: 'Die Advoware Schnittstelle',
      enableAdvoware: 'Advoware Integration aktivieren',
      advowareLawfirmId: 'Online Kennung',
      advowareLawfirmIdPlaceholder: 'Online Kennung eingeben',
      advowareDatabase: 'Datenbank',
      advowareDatabasePlaceholder: 'Datenbank eingeben',
      advowareUser: 'Nutzer (Kürzel)',
      advowareUserPlaceholder: 'Nutzer (Kürzel) eingeben',
      advowarePassword: 'Passwort',
      advowarePasswordPlaceholder: 'Passwort eingeben',
      saving: 'Speichern...',
      syncAdvoware: 'Advoware synchronisieren',
      success: 'Erfolg',
      syncSuccessMsg: 'Advoware wurde erfolgreich synchronisiert.',
      syncFailedMsg: 'Advoware-Synchronisierung fehlgeschlagen',
      advowareAuthError:
        'Advoware-Authentifizierung fehlgeschlagen. Dies kann an ungültigen Zugangsdaten, einer nicht verbundenen Advoware-Schnittstelle oder Netzwerkproblemen liegen. Bitte überprüfen Sie Ihre Einstellungen unter Einstellungen → Kanzleisoftware → Advoware und stellen Sie sicher, dass Ihr Advoware-System erreichbar ist.',
      advowareApiError:
        'Advoware API-Fehler: Es gab ein Problem bei der Kommunikation mit der Advoware-Schnittstelle. Bitte überprüfen Sie Ihre Verbindung und Einstellungen.'
    }
  })

  // Validation schema content removed as it is not needed;
  // all fields are optional and have no format rule.
  const validationSchema = {}

  interface FormData {
    advoware_lawfirm_id: string
    advoware_database: string
    advoware_user: string
    advoware_password: string
  }

  const { formData, formErrors, touchedFields, validateField, handleFieldChange } =
    useFormWithValidation<FormData>(
      {
        advoware_lawfirm_id: '',
        advoware_database: '',
        advoware_user: '',
        advoware_password: ''
      },
      validationSchema
    )

  const internalPartialAdvowareUpdate = async (data: AdvowareSettingsRequest) => {
    if (!user.value) {
      throw Error('User is not defined. Cannot update Advoware settings.')
    }

    await SettingsService.settingsAdvowareCreate({
      requestBody: data
    })
  }

  const updateAdvowareSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: AdvowareSettingsRequest = {
        is_advoware_export_integration_enabled: isAdvowareEnabled.value
      }

      // Only include lawfirm id if it has a value
      if (advowareLawfirmId.value) {
        data.advoware_lawfirm_id = advowareLawfirmId.value
      }

      // Only include database if it has a value
      if (advowareDatabase.value) {
        data.advoware_database = advowareDatabase.value
      }

      // Only include the user field if it has a value
      if (advowareUser.value) {
        data.advoware_user = advowareUser.value
      }

      // Only include the password field if it has changed
      if (advowarePassword.value !== originalAdvowarePassword.value) {
        data.advoware_password = advowarePassword.value
      }

      await internalPartialAdvowareUpdate(data)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const advowareSettings: AdvowareSettings =
        (await SettingsService.settingsAdvowareList()) as AdvowareSettings

      // Initialize form with existing values if available
      if (advowareSettings) {
        isAdvowareEnabled.value = advowareSettings.is_advoware_export_integration_enabled || false
        advowareLawfirmId.value = advowareSettings.advoware_lawfirm_id || ''
        formData.advoware_lawfirm_id = advowareLawfirmId.value
        advowareDatabase.value = advowareSettings.advoware_database || ''
        formData.advoware_database = advowareDatabase.value
        advowareUser.value = advowareSettings.advoware_user || ''
        formData.advoware_user = advowareUser.value
        advowarePassword.value = advowareSettings.advoware_password || ''
        originalAdvowarePassword.value = advowareSettings.advoware_password || ''
        formData.advoware_password = advowarePassword.value
      }
    } catch (error) {
      console.error('Error loading Advoware settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isAdvowareEnabled], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateAdvowareSettings()
    }
  })

  // Add watchers for the input fields
  watch(advowareLawfirmId, (newValue) => {
    formData.advoware_lawfirm_id = newValue
    handleFieldChange('advoware_lawfirm_id', newValue)
    if (user.value) {
      updateAdvowareSettings()
    }
  })

  watch(advowareDatabase, (newValue) => {
    formData.advoware_database = newValue
    handleFieldChange('advoware_database', newValue)
    if (user.value) {
      updateAdvowareSettings()
    }
  })

  watch(advowareUser, (newValue) => {
    formData.advoware_user = newValue
    handleFieldChange('advoware_user', newValue)
    if (user.value) {
      updateAdvowareSettings()
    }
  })

  watch(advowarePassword, (newValue) => {
    formData.advoware_password = newValue
    handleFieldChange('advoware_password', newValue)
    if (user.value) {
      updateAdvowareSettings()
    }
  })

  const isAdvowareSyncable = computed(() => {
    return (
      advowareLawfirmId.value &&
      advowareDatabase.value &&
      advowareUser.value &&
      advowarePassword.value
    )
  })

  const syncAdvoware = async () => {
    try {
      await SettingsService.settingsAdvowareSyncUpdate()
      toast({
        title: t('success'),
        description: t('syncSuccessMsg'),
        variant: 'default'
      })
    } catch (error: any) {
      Sentry.captureException(error)
      const statusCode = error?.status || null
      let errorDescription = t('syncFailedMsg')

      if (statusCode === HttpStatus.UNAUTHORIZED) {
        errorDescription = t('advowareAuthError')
      } else {
        errorDescription = t('advowareApiError')
      }

      toast({
        title: 'Error',
        description: errorDescription,
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== RoleEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- Advoware Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable Advoware Integration -->
          <CheckboxField
            v-model="isAdvowareEnabled"
            name="is_advoware_export_integration_enabled"
            :label="t('enableAdvoware')"
          />
          <!-- Advoware settings (only shown if Advoware is enabled) -->
          <div v-if="isAdvowareEnabled">
            <InputField
              v-model="advowareLawfirmId"
              name="advoware_lawfirm_id"
              :label="t('advowareLawfirmId')"
              :placeholder="t('advowareLawfirmIdPlaceholder')"
            />

            <InputField
              v-model="advowareDatabase"
              name="advoware_database"
              :label="t('advowareDatabase')"
              :placeholder="t('advowareDatabasePlaceholder')"
            />

            <InputField
              v-model="advowareUser"
              name="advoware_user"
              :label="t('advowareUser')"
              :placeholder="t('advowareUserPlaceholder')"
            />

            <InputField
              v-model="advowarePassword"
              name="advoware_password"
              :label="t('advowarePassword')"
              :placeholder="t('advowarePasswordPlaceholder')"
              type="password"
            />

            <button
              class="mt-5 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              :disabled="!isAdvowareSyncable"
              @click="syncAdvoware"
            >
              {{ t('syncAdvoware') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
